import { useEffect, useRef, useState } from 'react';

import { ApiError } from '@/api/rest';
import type { DeckBundle, DeckBundleInfo } from '@/api/rest';
import { useAppRuntime } from '@/app/runtime';
import { cx } from '@/ui/cx';
import { NoticeLine, type Notice } from '@/ui/notice';

import { summarizeBundle } from './deck-summary';
import { cardsWord, describeDeckError } from './shared';

export interface DeckPickerProps {
  /** Выбранная версия пака; `null` — ещё не выбрана, поле в запрос не уедет. */
  value: string | null;
  onChange: (version: string) => void;
}

/**
 * Витрина паков: раздел «ПАК» в настройке партии. Список версий приходит
 * из `GET /decks`, содержимое выбранного — из `GET /decks/{version}`.
 *
 * Своего экрана у витрины нет намеренно: в эталоне (`Клоун.dc.html`, 02)
 * «КОЛОДЫ» — это тумблеры четырёх `game.Deck`, а не паки, и отдельный узел
 * навигации под них пришлось бы заводить в обход ADR-0001, который ещё
 * черновик.
 *
 * Пак по умолчанию — первый в выдаче: порядок задаёт сервер, клиент его не
 * пересортировывает и ничего не выбирает сам (CLAUDE.md, инвариант 1).
 * Бандл живёт в памяти компонента и никуда не сохраняется — в localStorage
 * лежат только код комнаты и возрастной гейт (инвариант 5).
 */
export function DeckPicker({ value, onChange }: DeckPickerProps) {
  const restClient = useAppRuntime()?.restClient ?? null;

  const [decks, setDecks] = useState<DeckBundleInfo[] | null>(null);
  const [listNotice, setListNotice] = useState<Notice | null>(null);
  const [open, setOpen] = useState(false);
  const [bundles, setBundles] = useState<Record<string, DeckBundle>>({});
  const [bundleNotice, setBundleNotice] = useState<Notice | null>(null);

  // Колбэк не должен попадать в зависимости эффекта: список тянется один раз
  // на клиента, а не заново на каждый ререндер родителя.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!restClient) return;
    let cancelled = false;

    restClient.listDecks().then(
      (list) => {
        if (cancelled) return;
        setDecks(list);
        const first = list[0];
        if (first) onChangeRef.current(first.version);
      },
      (err: unknown) => {
        if (!cancelled) setListNotice(noticeFor(err));
      },
    );

    return () => {
      cancelled = true;
    };
  }, [restClient]);

  useEffect(() => {
    if (!restClient || value === null || bundles[value]) return;
    let cancelled = false;
    setBundleNotice(null);

    restClient.getDeckBundle(value).then(
      (bundle) => {
        if (!cancelled) setBundles((prev) => ({ ...prev, [value]: bundle }));
      },
      (err: unknown) => {
        if (!cancelled) setBundleNotice(noticeFor(err));
      },
    );

    return () => {
      cancelled = true;
    };
  }, [restClient, value, bundles]);

  const selected = decks?.find((deck) => deck.version === value) ?? null;
  const bundle = value === null ? undefined : bundles[value];
  // Пустая витрина — рабочее состояние свежей базы: контент ещё не залит.
  // Партии это не мешает, версию тогда выбирает сервер.
  const empty = decks?.length === 0;

  return (
    <section className="mt-6">
      <p className="text-meta uppercase text-mute">Пак</p>

      {!restClient && <p className="mt-2 text-base text-mute">Список паков недоступен офлайн.</p>}
      {listNotice && <NoticeLine notice={listNotice} />}
      {empty && <p className="mt-2 text-base text-mute">Паков пока нет — колоду выберет сервер.</p>}
      {restClient && !decks && !listNotice && (
        <p className="mt-2 text-base text-mute">Загружаем список…</p>
      )}

      {selected && (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          className="mt-2 flex min-h-tap w-full appearance-none items-center gap-3 rounded-none border-2 border-line bg-panel px-4 text-left"
        >
          <span className="flex-1 text-row">{deckTitle(selected)}</span>
          {selected.paid && <span className="text-meta uppercase text-mute">Платно</span>}
          <span className={cx('text-lead text-mute', open && 'text-acc')}>{open ? '×' : '›'}</span>
        </button>
      )}

      {open && decks && (
        <div className="mt-rule flex flex-col gap-rule border-2 border-line bg-line">
          {decks.map((deck) => {
            const active = deck.version === value;
            return (
              <button
                key={deck.version}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  onChange(deck.version);
                  setOpen(false);
                }}
                className="flex min-h-tap appearance-none items-center gap-3 rounded-none bg-panel px-4 text-left"
              >
                <span className={cx('text-lead', active ? 'text-acc' : 'text-mute')}>
                  {active ? '●' : '○'}
                </span>
                <span className={cx('flex-1 text-row', active ? 'text-ink' : 'text-dim')}>
                  {deckTitle(deck)}
                </span>
                {deck.paid && <span className="text-meta uppercase text-mute">Платно</span>}
              </button>
            );
          })}
        </div>
      )}

      {/*
        Отказ показывается только пока показывать нечего: бандл из кэша
        отдаётся без запроса, а значит и без сброса ошибки, и иначе счётчики
        одного пака ехали бы вместе с отказом другого.
      */}
      {bundleNotice && !bundle && <NoticeLine notice={bundleNotice} />}
      {bundle && <DeckContents bundle={bundle} />}
    </section>
  );
}

function noticeFor(err: unknown): Notice {
  if (err instanceof ApiError && err.isNotImplemented) {
    return { text: 'Выбор пака пока недоступен', kind: 'absent' };
  }
  return { text: describeDeckError(err), kind: 'error' };
}

/** Название пака; пока `title` не проставлен, различать паки нечем, кроме версии. */
function deckTitle(deck: DeckBundleInfo): string {
  return deck.title ?? deck.version;
}

/**
 * Что внутри пака — счётчиками. Тексты заданий не показываем: прочитанная
 * заранее карточка в партии уже не сыграет.
 */
function DeckContents({ bundle }: { bundle: DeckBundle }) {
  const summary = summarizeBundle(bundle);

  return (
    <div className="mt-3">
      <p className="text-meta uppercase text-mute">
        {summary.total} {cardsWord(summary.total)}
      </p>

      <div className="mt-2 flex flex-col gap-rule border-2 border-line bg-line">
        {summary.decks.map((deck) => (
          <div key={deck.deck} className="flex h-row items-center gap-3 bg-panel px-4">
            <span className="text-lead">{deck.emoji}</span>
            <span className={cx('h-6 w-3', deck.swatch)} />
            <span className="flex-1 text-lead">{deck.label}</span>
            <span className="text-lead text-dim">{deck.count}</span>
          </div>
        ))}
      </div>

      <p className="mt-3 text-base text-dim">
        <span className="text-mute">Уровень: </span>
        {summary.intensity.map((level) => `${level.label} ${level.count}`).join(' · ')}
      </p>

      {summary.requirements.length > 0 && (
        <p className="mt-1 text-base text-dim">
          <span className="text-mute">Реквизит: </span>
          {summary.requirements.join(' · ')}
        </p>
      )}

      {bundle.minigames && bundle.minigames.length > 0 && (
        <p className="mt-1 text-base text-dim">
          <span className="text-mute">Мини-игры: </span>
          {bundle.minigames.join(' · ')}
        </p>
      )}
    </div>
  );
}
